<template>
  <v-alert
    v-if="
      transactionsStore.transactionsError &&
      transactionsStore.transactionsError !== 'Too many pages scanned'
    "
    type="error"
  >
    {{ t("transactions.error", { email: "help@yetipay.me" }) }}
  </v-alert>

  <v-skeleton-loader v-else-if="!transactionsStore.transactions" type="table" />

  <div v-else>
    <v-alert
      v-if="transactionsStore.transactionsError === 'Too many pages scanned'"
      type="error"
      density="compact"
      class="mb-3"
    >
      {{ t("transactions.tooManyPages", { email: "help@yetipay.me" }) }}
    </v-alert>
    <div
      class="w-100 d-flex flex-column flex-sm-row ga-4 align-center justify-space-between mb-1 mb-sm-0"
    >
      <h3 class="text-primary">
        <span class="on-background-variant">{{ t("transactions.todayTotal") }}</span>
        <span v-if="transactionsStore.transactionsTotals?.totalsAmount">
          {{ displayAmount(transactionsStore.transactionsTotals?.totalsAmount) }}
          <span v-if="transactionsStore.transactionsTotals?.tipsAmount.value > 0">
            ({{ displayAmount(transactionsStore.transactionsTotals?.tipsAmount) }}
            {{ t("views.transactions.tips") }})
          </span>
        </span>
        <span v-else class="mx-1">-</span>
        <v-tooltip
          :text="transactionsTotalsTooltip!"
          :disabled="!transactionsTotalsTooltip"
          location="bottom"
        >
          <template #activator="{ props }">
            <span v-bind="props"
              ><v-icon v-if="transactionsTotalsTooltip" color="primary"
                >mdi-information-outline</v-icon
              ></span
            >
          </template>
        </v-tooltip>
      </h3>

      <SiteSelector
        v-model="transactionsStore.transactionsParams.siteId"
        class="w-100 mt-0 mt-sm-4"
        style="max-width: 250px"
      />
    </div>

    <FiltersDrawer v-model="isDrawerOpen" />

    <v-alert
      v-if="transactionsStore.transactionsNotAllowed"
      type="warning"
      variant="outlined"
      density="compact"
    >
      {{ t("transactions.selectSite") }}
    </v-alert>

    <v-data-table
      :headers="transactionsTableHeaders"
      :items="transactionsStore.transactions"
      :loading="transactionsStore.transactionsLoading"
      :loading-text="t('transactions.loading')"
      :no-data-text="t('transactions.noData')"
      :sort-by="[{ key: 'createdDate', order: 'desc' }]"
      item-value="pspReference"
      items-per-page="-1"
      :expanded="[]"
      show-expand
      expand-on-click
    >
      <template #top>
        <div class="d-flex justify-space-between ga-2 mt-1 flex-column flex-sm-row">
          <FilterBadges />

          <div class="d-flex ga-2 justify-end">
            <v-tooltip location="bottom" :disabled="!reportDisabled">
              <template #activator="{ props }">
                <div v-bind="props">
                  <v-btn
                    variant="text"
                    size="default"
                    color="secondary"
                    :disabled="reportDisabled"
                    :loading="reportLoading"
                    icon="mdi-download"
                    @click="downloadReport"
                  />
                </div>
              </template>
              {{ reportDisabledText }}
            </v-tooltip>
            <v-btn
              variant="text"
              size="default"
              color="secondary"
              icon="mdi-refresh"
              @click="transactionsStore.refresh"
            />

            <v-badge :content="paramsCount" color="primary" offset-y="5" offset-x="5">
              <v-btn
                variant="text"
                size="default"
                color="secondary"
                icon="mdi-filter-cog"
                @click.stop="isDrawerOpen = !isDrawerOpen"
              />
            </v-badge>
          </div>
        </div>
      </template>

      <template #expanded-row="{ columns, item }">
        <td :colspan="columns.length" class="pa-4">
          <div class="d-flex flex-grow-1 d-flex flex-column flex-md-row ga-md-4">
            <div class="d-flex flex-column">
              <p>
                <b>{{ t("transactions.transactionId") }}</b> <kbd>{{ item.transactionId }}</kbd>
              </p>
              <p>
                <b>{{ t("transactions.merchantReference") }}</b> {{ item.adyenMerchantReference }}
              </p>
              <p v-if="!item.successBool">
                <b>{{ t("transactions.reason") }}</b> {{ item.reason }}
              </p>
            </div>

            <div v-if="item.paymentLinkId" class="d-flex flex-column flex-grow-1">
              <p>
                <b>{{ t("transactions.paymentLinkId") }}</b> <kbd>{{ item.paymentLinkId }}</kbd>
              </p>
              <p>
                <b>{{ t("transactions.paymentReference") }}</b>
                {{ transactionsStore.paymentLinks[item.paymentLinkId] || "-" }}
              </p>
            </div>
          </div>

          <div v-if="item.refunds">
            <p class="font-weight-bold text-uppercase text-primary mt-3">
              {{ t("transactions.refunds") }}
            </p>
            <v-table density="compact" hover>
              <template #default>
                <thead>
                  <tr>
                    <th class="text-left">
                      {{ t("views.transactions.pspReference") }}
                    </th>
                    <th class="text-left">
                      {{ t("transactions.bookingDate") }}
                    </th>
                    <th class="text-left">
                      {{ t("views.transactions.amount") }}
                    </th>
                    <th class="text-left">
                      {{ t("transactions.actions") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="refund in item.refunds" :key="refund.pspReference">
                    <td>
                      <kbd>{{ refund.pspReference }}</kbd>
                    </td>
                    <td>{{ refund.bookingDate }}</td>
                    <td>{{ displayAmount(refund.amount, true) }}</td>
                    <td>
                      <v-btn-nd
                        size="small"
                        variant="tonal"
                        @click="
                          refundSelectedForEmail = refund;
                          refundMainPspReference = item.pspReference;
                          refundEmailDialog = true;
                        "
                      >
                        {{ t("transactions.sendEmail") }}
                      </v-btn-nd>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
          </div>
        </td>
      </template>
      <template #item.paymentSource="{ item }">
        <div class="d-flex align-center">
          <span>{{
            t(`components.transactions.filters.paymentSource.${item.paymentSource}`)
          }}</span>
        </div>
      </template>
      <template #item.pspReference="{ item }">
        <div class="d-flex align-center">
          <span>{{ item.pspReference }}</span>
        </div>
      </template>
      <template #item.paymentMethod="{ item }">
        <payment-method :method="item.paymentMethod" />
      </template>
      <template #item.cardSummary="{ item }">
        <kbd><sup>****</sup>{{ item.cardSummary }}</kbd>
      </template>
      <template #item.amount="{ item }">
        <div class="d-flex align-center">
          {{ displayAmount(item.amount) }}
        </div>
      </template>
      <template #item.tip="{ item }">
        {{ displayAmount(item.includedTipAmount ?? { value: 0, currency: item.amount.currency }) }}
      </template>
      <template #item.status="{ item }">
        <span>{{ t(`transactions.status.${item.status}`) }}</span>
      </template>
      <template #item.actions="{ item }">
        <v-btn-nd
          :disabled="!item.enableRefund"
          @click="
            transactionSelectedForRefund = item;
            refundConfirmDialog = true;
          "
        >
          {{ t("transactions.refund") }}
        </v-btn-nd>
      </template>
      <template #bottom />
    </v-data-table>

    <v-pagination
      v-model="transactionsStore.page"
      :length="transactionsStore.totalPages"
      :disabled="transactionsStore.transactionsLoading"
      color="secondary"
      rounded="sm"
    >
      <template #next="{ disabled, onClick }">
        <v-btn-nd
          v-if="!transactionsStore.finalPageKnown"
          disabled
          icon="mdi-dots-horizontal"
          variant="text"
          size="default"
        />
        <v-btn-nd
          :disabled="disabled || transactionsStore.isFinalPage"
          icon="mdi-chevron-right"
          variant="text"
          size="default"
          @click="onClick"
        />
      </template>
    </v-pagination>
  </div>

  <RefundDialog v-model="refundConfirmDialog" :transaction="transactionSelectedForRefund" />
  <EmailRefundDialog
    v-model="refundEmailDialog"
    :value="refundSelectedForEmail?.amount.value || 0"
    :currency="refundSelectedForEmail?.amount.currency || 'GBP'"
    :psp-reference="refundMainPspReference || ''"
    :refund-psp-reference="refundSelectedForEmail?.pspReference || ''"
  />
  <DownloadDialog v-model="reportDownloadDialog" :report-id="reportId" />
</template>

<script setup lang="ts">
import {
  AddSitesPaymentMethodRequestPaymentMethodEnum,
  TransactionRefund,
  PaymentSource,
  TransactionStatusEnum,
} from "@tableyeti/merchant-service";
import BigNumber from "bignumber.js";
import { differenceInDays, endOfDay, startOfDay } from "date-fns";
import { ref, onBeforeMount, onMounted, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { Amount } from "@/api/merchant-service/common-types";
import { TransactionV1 } from "@/api/merchant-service/transaction";
import { useTransactionParams } from "@/composables/useTransactionParams.ts";
import { useYetipayApi } from "@/composables/useYetipayApi.ts";
import { useMerchantsStore } from "@/store/merchants";
import { useTransactionsStore, prepareDatesForApi } from "@/store/transactions";

const route = useRoute();
const { t } = useI18n();
const transactionsStore = useTransactionsStore();
const merchantsStore = useMerchantsStore();
const { paramsCount } = useTransactionParams();

const transactionsTableHeaders = [
  { title: t("transactions.headers.paymentSource"), value: "paymentSource" },
  { title: t("transactions.headers.pspReference"), value: "pspReference" },
  { title: t("transactions.headers.site"), value: "siteName" },
  { title: t("transactions.headers.status"), value: "status" },
  { title: t("transactions.headers.amount"), value: "amount" },
  { title: t("transactions.headers.tip"), value: "tip" },
  { title: t("transactions.headers.cardSummary"), value: "cardSummary" },
  { title: t("transactions.headers.paymentMethod"), value: "paymentMethod" },
  { title: t("transactions.headers.createdDate"), value: "createdDate" },
  { title: t("transactions.headers.actions"), value: "actions" },
];

const isDrawerOpen = ref(false);

const transactionSelectedForRefund = ref<TransactionV1 | undefined>(undefined);
const refundConfirmDialog = ref(false);

onBeforeMount(() => {
  if (transactionsStore.transactions) {
    // In the case where the store is already initialized, we don't want to override the params
    saveParamsToUrl();
    transactionsStore.refresh();
  } else {
    // Changing the params here will trigger a refresh (see the watch below)
    if (route.query.psp) {
      transactionsStore.transactionsParams.pspReference = route.query.psp as string;
      // Reset remaining filters since psp search is specific to a single trx
      transactionsStore.transactionsParams.paymentSource = undefined;
      transactionsStore.transactionsParams.statuses = undefined;
      transactionsStore.transactionsParams.cardSummary = undefined;
      transactionsStore.transactionsParams.paymentMethods = undefined;
      return;
    }
    if (route.query.paymentSource) {
      const paymentSource = route.query.paymentSource as PaymentSource;
      transactionsStore.transactionsParams.paymentSource = paymentSource;
    }
    if (route.query.statuses) {
      const statuses = (route.query.statuses as string).split(",") as TransactionStatusEnum[];
      transactionsStore.transactionsParams.statuses = statuses;
    }
    if (route.query.cardSummary) {
      const cardSummary = route.query.cardSummary as string;
      transactionsStore.transactionsParams.cardSummary = +cardSummary;
    }
    if (route.query.paymentMethods) {
      const paymentMethods = (route.query.paymentMethods as string).split(
        ",",
      ) as AddSitesPaymentMethodRequestPaymentMethodEnum[];
      transactionsStore.transactionsParams.paymentMethods = paymentMethods;
    }
    if (route.query.siteId) {
      transactionsStore.transactionsParams.siteId = route.query.siteId as string;
    }
    if (route.query.dates) {
      transactionsStore.transactionsParams.dates = (route.query.dates as string)
        .split(",")
        .map((date: string) => new Date(date));
    } else {
      transactionsStore.transactionsParams.dates = [startOfDay(new Date()), endOfDay(new Date())]; // Today
    }
  }
});

watch(
  () => transactionsStore.page,
  () => transactionsStore.setPage(transactionsStore.page),
);

onMounted(() => transactionsStore.getTransactionTotals());
watch(
  () => transactionsStore.transactionsParams.siteId,
  () => transactionsStore.getTransactionTotals(),
);

watch(
  () => transactionsStore.transactionsParams,
  () => {
    if (transactionsStore.transactionsParams.pspReference) {
      transactionsStore.transactionsParams.paymentSource = undefined;
      transactionsStore.transactionsParams.dates = undefined;
      transactionsStore.transactionsParams.paymentMethods = undefined;
      transactionsStore.transactionsParams.statuses = undefined;
      transactionsStore.transactionsParams.cardSummary = undefined;
    }
    saveParamsToUrl();
    transactionsStore.refresh();
  },
  { deep: true },
);

const transactionsTotalsTooltip = computed(() => {
  if (!transactionsStore.transactionsParams.siteId) {
    return t("transactions.tooltip.siteNotSelected");
  } else if (!transactionsStore.transactionsTotals) {
    return undefined; // still loading
  }
  if (transactionsStore.transactionsTotals.totalsCount === 0) {
    return t("transactions.tooltip.noTransactions");
  }
  const { salesDayClosingTime } = merchantsStore.currentMerchant?.settings || {};
  return t("transactions.tooltip.info", {
    closingTime: salesDayClosingTime ?? t("transactions.tooltip.midnight"),
  });
});

const saveParamsToUrl = () => {
  const queryParams: string[] = [];
  if (
    transactionsStore.transactionsParams.dates &&
    transactionsStore.transactionsParams.dates.length > 0
  ) {
    // VueDatePicker sets the second date as null when only 1 is selected
    queryParams.push(
      `dates=${transactionsStore.transactionsParams.dates
        .filter((d) => !!d)
        .map((date: Date) => date.toISOString())
        .join(",")}`,
    );
  }
  if (transactionsStore.transactionsParams.paymentSource) {
    queryParams.push(`paymentSource=${transactionsStore.transactionsParams.paymentSource}`);
  }
  if (transactionsStore.transactionsParams.siteId) {
    queryParams.push(`siteId=${transactionsStore.transactionsParams.siteId}`);
  }
  if (transactionsStore.transactionsParams.statuses) {
    queryParams.push(`statuses=${transactionsStore.transactionsParams.statuses.join(",")}`);
  }
  if (transactionsStore.transactionsParams.paymentMethods) {
    queryParams.push(
      `paymentMethods=${transactionsStore.transactionsParams.paymentMethods.join(",")}`,
    );
  }
  if (
    transactionsStore.transactionsParams.cardSummary &&
    /^\d{4}$/.test(`${transactionsStore.transactionsParams.cardSummary}`)
  ) {
    queryParams.push(`cardSummary=${transactionsStore.transactionsParams.cardSummary}`);
  }
  if (transactionsStore.transactionsParams.pspReference) {
    queryParams.push(`psp=${transactionsStore.transactionsParams.pspReference}`);
  }
  history.pushState(
    {},
    "",
    route.path + (queryParams.length > 0 ? "?" + queryParams.join("&") : ""),
  );
};

const displayAmount = (amount: Amount, invert = false) => {
  return new BigNumber(amount.value)
    .dividedBy(100)
    .multipliedBy(invert ? -1 : 1)
    .toNumber()
    .toLocaleString("en-UK", {
      style: "currency",
      currency: amount.currency,
    });
};

const reportLoading = ref(false);
const reportDisabled = computed(() => {
  const noResult = transactionsStore.transactions?.length === 0;
  const noDatesNorPsp =
    !transactionsStore.transactionsParams.dates &&
    !transactionsStore.transactionsParams.pspReference;
  const bothDatesAndPsp =
    !!transactionsStore.transactionsParams.dates &&
    !!transactionsStore.transactionsParams.pspReference;
  const datesTooFarApart =
    transactionsStore.transactionsParams.dates?.length === 2 &&
    differenceInDays(
      transactionsStore.transactionsParams.dates[1],
      transactionsStore.transactionsParams.dates[0],
    ) > 186;
  return noResult || reportLoading.value || noDatesNorPsp || bothDatesAndPsp || datesTooFarApart;
});
const reportDisabledText = computed(() => {
  if (reportLoading.value) {
    return t("transactions.generatingReport");
  }
  if (transactionsStore.transactionsLoading) {
    return t("transactions.loadingTransactions");
  }
  if (transactionsStore.transactions?.length === 0) {
    return t("transactions.noTransactions");
  }
  if (
    !transactionsStore.transactionsParams.dates &&
    !transactionsStore.transactionsParams.pspReference
  ) {
    return t("transactions.selectDateRange");
  }
  if (
    !!transactionsStore.transactionsParams.dates &&
    !!transactionsStore.transactionsParams.pspReference
  ) {
    return t("transactions.selectEither");
  }
  if (
    transactionsStore.transactionsParams.dates?.length === 2 &&
    differenceInDays(
      transactionsStore.transactionsParams.dates[1],
      transactionsStore.transactionsParams.dates[0],
    ) > 186
  ) {
    return t("transactions.dateRangeTooLarge");
  }
  return t("transactions.generatingReport");
});

const reportId = ref<string | undefined>(undefined);
const reportDownloadDialog = ref(false);
const downloadReport = async () => {
  const { api, currentMerchantId } = useYetipayApi();
  const currentMerchant = useMerchantsStore().currentMerchant!;
  reportLoading.value = true;
  try {
    const result = await api.createReport(currentMerchantId, {
      dataType: "transactions",
      dates: prepareDatesForApi(
        transactionsStore.transactionsParams.dates,
        currentMerchant.settings?.salesDayClosingTime,
      ),
      siteId: transactionsStore.transactionsParams.siteId || undefined,
      psp: transactionsStore.transactionsParams.pspReference,
      paymentSource: transactionsStore.transactionsParams.paymentSource,
      paymentMethods: transactionsStore.transactionsParams.paymentMethods?.join(",") || undefined,
      statuses: transactionsStore.transactionsParams.statuses?.join(",") || undefined,
      cardSummary: transactionsStore.transactionsParams.cardSummary
        ? `${transactionsStore.transactionsParams.cardSummary}`
        : undefined,
    });
    reportId.value = result.data.reportId;
    reportDownloadDialog.value = true;
  } catch (e) {
    console.error(e);
  } finally {
    reportLoading.value = false;
  }
};

const refundMainPspReference = ref<string | undefined>(undefined);
const refundSelectedForEmail = ref<TransactionRefund | undefined>(undefined);
const refundEmailDialog = ref(false);
</script>
